import { Icon } from '@mdvip/shared/components/blocks/Icon';
import { Button } from '@mdvip/shared/components/ui/button';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from '@mdvip/shared/components/ui/dialog';
import { cn } from '@mdvip/shared/lib/utils';
import type { ReactNode } from 'react';
import * as React from 'react';

/**
 * Whether the close button renders at all, shared between `ResponsiveModal` (which owns the
 * desktop floating instance) and `ResponsiveModalHeader` (which owns the mobile in-row instance —
 * see the header's doc comment for why there are two).
 */
const ResponsiveModalContext = React.createContext(true);

interface ResponsiveModalBaseProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Body/header/footer slots — compose with the `ResponsiveModal*` parts below. */
  children: ReactNode;
  /**
   * Desktop (centered-dialog) max-width override, e.g. `sm:max-w-2xl`. Applied to the dialog shell
   * only — the mobile take-over is always edge-to-edge, so a width class would wrongly narrow it in
   * the 640–767px range.
   */
  className?: string;
  showCloseButton?: boolean;
}

// Discriminated union so a caller can't pass `onBack` without `backLabel` (an icon-only back
// button with no accessible name) — the pairing is enforced at the call site, not by convention.
type ResponsiveModalProps = ResponsiveModalBaseProps &
  (
    | {
        /**
         * Shows a back arrow wired to this handler. Desktop-only (`hidden sm:inline-flex`) —
         * matches the design, which gives mobile no header back affordance at all; a back-able
         * mobile screen relies on an in-body/footer action instead (e.g. Review's "Make changes").
         */
        onBack: () => void;
        /** Accessible label for the back button — required together with `onBack`. */
        backLabel: string;
      }
    | { onBack?: never; backLabel?: never }
  );

/**
 * A modal that presents as a full-screen takeover on narrow viewports and a centered dialog on
 * wide ones.
 *
 * The choice is driven by **CSS `sm:` breakpoint classes** (640px), not a JS viewport check or a
 * platform check — one `Dialog` (`variant="takeover"`) renders both shapes; there's no component
 * swap. Both shapes lay out as a flex column (pinned header / scrolling body / pinned footer)
 * capped at the dynamic viewport height, so on the native app the sticky footer rides above the
 * keyboard (see `lib/native-keyboard.ts` + `docs/020-mobile.md` §8).
 *
 * Note: this used to swap between `Dialog` and `Sheet` at a JS-detected 768px breakpoint (see git
 * history if you need the old behavior). That meant the shell's own component boundary (768px) and
 * its internal Tailwind `sm:` classes (640px) disagreed in the 640–767px window. Moving to a single
 * takeover `Dialog` removes that seam and matches the design system's own `ModalShell`, which is
 * also one component with pure CSS breakpoints.
 *
 * Requires a `ResponsiveModalTitle` child for an accessible label — the underlying Base UI dialog
 * warns (and is unlabeled) without one.
 */
function ResponsiveModal({
  open,
  onOpenChange,
  className,
  showCloseButton = true,
  onBack,
  backLabel,
  children,
}: ResponsiveModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        variant="takeover"
        showCloseButton={false}
        overlayClassName="p-0 sm:px-6 sm:py-[100px]"
        className={cn(
          'flex h-full w-full flex-col sm:h-auto sm:max-h-[85dvh] sm:min-w-[424px] sm:overflow-hidden sm:rounded-[24px] sm:border sm:shadow-2xl',
          className,
        )}
      >
        <ResponsiveModalContext.Provider value={showCloseButton}>
          {onBack && (
            <Button
              type="button"
              variant="ghost"
              size="icon-lg"
              className="absolute top-3 left-3 z-10 hidden rounded-full text-muted-foreground opacity-70 hover:opacity-100 sm:inline-flex"
              aria-label={backLabel}
              onClick={onBack}
            >
              <Icon name="chevronLeft" className="text-[19px]" />
            </Button>
          )}
          {/* Desktop-only floating close — decoupled from the title's flow (matches the design's
              corner-anchored close). The mobile instance lives inline in `ResponsiveModalHeader`,
              flex-centered against the title instead of pinned to a fixed pixel offset, so it
              can't drift out of vertical alignment when the title wraps or the header padding
              changes. Two `DialogClose` triggers for one dialog is fine — no uniqueness constraint
              like the title/description ids. */}
          {showCloseButton && (
            <DialogClose
              data-slot="dialog-close"
              render={
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-lg"
                  className="absolute top-3 right-3 z-10 hidden rounded-full text-muted-foreground opacity-70 hover:bg-accent hover:opacity-100 sm:inline-flex"
                  aria-label="Close"
                />
              }
            >
              <Icon name="x" className="text-[21px]" />
            </DialogClose>
          )}
          {children}
        </ResponsiveModalContext.Provider>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The header row. A single `ResponsiveModalTitle`/`ResponsiveModalDescription` pair is styled for
 * both breakpoints via descendant selectors — deliberately not the design's literal markup, which
 * renders the title block twice (once per breakpoint, toggled by `sm:hidden`/`hidden sm:block`) so
 * CSS alone can pick the active copy. Duplicating a Base UI `DialogTitle`/`DialogDescription` would
 * risk two elements racing for the dialog's single `aria-labelledby`/`aria-describedby` id — not
 * worth it for a result that's otherwise identical.
 *
 * The close button is a different story: mobile renders its own in-row `DialogClose` here,
 * flex-centered against the title (`items-center`) — matching the design's app-bar, and avoiding
 * the vertical misalignment a fixed `top-4` offset would have against a title of unknown height.
 * `ResponsiveModal` renders the separate desktop-only floating instance; both read `showCloseButton`
 * from context so they can't drift out of sync with each other or with `showCloseButton={false}`.
 */
function ResponsiveModalHeader({ className, children, ...props }: React.ComponentProps<'div'>) {
  const showCloseButton = React.useContext(ResponsiveModalContext);
  return (
    <div
      data-slot="responsive-modal-header"
      className={cn(
        'flex items-center gap-3 border-b border-border px-4 py-3 sm:block sm:border-0 sm:px-8 sm:pt-8 sm:pr-14 sm:pb-0',
        className,
      )}
      {...props}
    >
      <div
        className={cn(
          'min-w-0 flex-1',
          '[&_[data-slot=dialog-title]]:text-2xl [&_[data-slot=dialog-title]]:font-semibold',
          '[&_[data-slot=dialog-description]]:hidden sm:[&_[data-slot=dialog-description]]:mt-2 sm:[&_[data-slot=dialog-description]]:block',
        )}
      >
        {children}
      </div>
      {showCloseButton && (
        <DialogClose
          data-slot="dialog-close"
          render={
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="shrink-0 rounded-full text-muted-foreground opacity-80 hover:opacity-100 sm:hidden"
              aria-label="Close"
            />
          }
        >
          <Icon name="x" size="lg" />
        </DialogClose>
      )}
    </div>
  );
}

/**
 * The single scroll region. `min-h-0 flex-1` lets it shrink within the height-capped column so the
 * body scrolls and the header/footer stay pinned (omitting `min-h-0` pushes the footer off-screen).
 * Kept as an internally-scrolling region at every breakpoint — the design lets its desktop body
 * grow past the viewport and scroll the whole overlay instead, but that would carry the sticky
 * footer's actions off-screen on a tall form, which this codebase treats as a hard requirement (see
 * `docs/020-mobile.md` §8). Deliberate deviation, not an oversight.
 */
function ResponsiveModalBody({ className, children, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="responsive-modal-body"
      className={cn('min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-5 sm:px-8 sm:py-6', className)}
      {...props}
    >
      {children}
    </div>
  );
}

/**
 * Pinned footer. `children` are the action buttons (laid out in their own reversible row so a
 * trailing `note` always renders last, not swapped by `flex-col-reverse`). Safe-area padding clears
 * the iOS home indicator (the modal portals outside `#root`).
 */
function ResponsiveModalFooter({
  className,
  children,
  note,
  ...props
}: React.ComponentProps<'div'> & { note?: ReactNode }) {
  return (
    <div
      data-slot="responsive-modal-footer"
      className={cn(
        'flex flex-col border-t border-border px-4 pt-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] sm:border-0 sm:px-8 sm:pt-0 sm:pb-8',
        className,
      )}
      {...props}
    >
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-3 [&>button]:w-full sm:[&>button]:w-auto sm:[&>button]:flex-1">
        {children}
      </div>
      {note && (
        <p className="mt-3 flex items-center justify-center gap-1.5 text-center text-xs text-muted-foreground">
          {note}
        </p>
      )}
    </div>
  );
}

// Title / Description / Close come from the shared Base UI Dialog primitive.
const ResponsiveModalTitle = DialogTitle;
const ResponsiveModalDescription = DialogDescription;
const ResponsiveModalClose = DialogClose;

export {
  ResponsiveModal,
  ResponsiveModalBody,
  ResponsiveModalClose,
  ResponsiveModalDescription,
  ResponsiveModalFooter,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
};
